import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { MemberRole } from '@mustauto/types/permissions';
import { api } from '@/lib/api';

export interface MechanicStats {
  id: string;
  name: string;
  phone: string | null;
  avatarUrl: string | null;
  activeToday: number;
  completedMonth: number;
  /** Nulos sem painel.faturamento. */
  revenue: number | null;
  avgTicket: number | null;
  avgExecutionHours: number;
  reworkRate: number;
  reworkCount: number;
  avgRating: number | null;
}

interface TeamStatsData {
  mechanics: MechanicStats[];
}

/** `enabled: false` para quem não tem `equipe.ver_desempenho` — a API responderia 403. */
export function useTeamStats(month: string, enabled = true) {
  return useQuery<TeamStatsData>({
    queryKey: ['team-stats', month],
    enabled,
    queryFn: async () => {
      const res = await api.get(`/workshops/my/team/stats?month=${month}`);
      return res.data;
    },
  });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    // Sem papel, Mecânico — o convite de sempre.
    mutationFn: async (role: MemberRole = 'mechanic') => {
      const res = await api.post('/workshops/my/invite', { role });
      // `url` vem pronta do servidor: é o host que serve a landing /e/ e os
      // arquivos .well-known do deep link. Montar a URL no cliente já quebrou
      // o convite uma vez (link apontava para um domínio sem a rota).
      return res.data as {
        code: string;
        role: MemberRole;
        url: string;
        revokedCount: number;
        // true = o código que já existia, com o prazo renovado (gerar de novo
        // não troca mais o código — o que está no grupo continua valendo).
        renewed: boolean;
        expiresAt: string;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-stats'] });
      queryClient.invalidateQueries({ queryKey: ['workshop-invites'] });
    },
  });
}

export interface ActiveInvite {
  code: string;
  role: MemberRole;
  expiresAt: string;
  url: string;
}

/**
 * Convites ativos da oficina. Existe para o gestor enxergar que já tem convite
 * válido antes de gerar outro — sem isso, mecânico que não entra vira uma pilha
 * de códigos vivos (ver docs/ref/mustauto_convites_plano.md).
 */
export function useActiveInvites(enabled = true) {
  return useQuery({
    queryKey: ['workshop-invites'],
    // Listar convites expõe tokens: só quem pode convidar pede (a API dá 403).
    enabled,
    queryFn: async () => {
      const res = await api.get('/workshops/my/invites');
      return (res.data as { invites: ActiveInvite[] }).invites;
    },
  });
}

export function useRevokeInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (code: string) => {
      await api.delete(`/workshops/my/invites/${code}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workshop-invites'] });
    },
  });
}
